import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  Car, Building2, BarChart3, FileCheck, Tag, Bell, 
  Calendar, CheckCircle, Settings2, Baby, Percent, Clock,
  CreditCard, Banknote, Mail, Receipt,
  FileText, PenTool, Send,
  ClipboardCheck, Fuel, Camera, Sparkles,
  BellRing, MessageSquare, AlertCircle,
  TrendingUp, PieChart, Activity, CalendarRange,
  User, Upload, ScanFace, History,
  Shield, Lock, Users,
  Smartphone, Monitor, Tablet,
  MapPin, Globe,
  ChevronDown, ChevronUp, Check
} from 'lucide-react';

const allFeatures = [
  {
    category: 'Gestion de flotte',
    categoryEn: 'Fleet Management',
    icon: Car,
    color: 'blue',
    features: [
      { icon: Building2, title: 'Gestion multi-agences', titleEn: 'Multi-agency management', desc: 'Genève, Lausanne, etc.' },
      { icon: BarChart3, title: 'Dashboard temps réel', titleEn: 'Real-time dashboard', desc: 'Statistiques en direct' },
      { icon: FileCheck, title: 'Fiches véhicules complètes', titleEn: 'Complete vehicle cards', desc: 'Photos, documents, caractéristiques' },
      { icon: Tag, title: 'Catégorisation véhicules', titleEn: 'Vehicle categorization', desc: 'Berline, SUV, utilitaire...' },
      { icon: Settings2, title: 'Suivi du statut', titleEn: 'Status tracking', desc: 'Disponible, loué, maintenance' },
      { icon: Bell, title: 'Alertes documents', titleEn: 'Document alerts', desc: 'Expiration assurance, CT' },
    ]
  },
  {
    category: 'Réservation en ligne',
    categoryEn: 'Online Booking',
    icon: Calendar,
    color: 'green',
    features: [
      { icon: Car, title: 'Réservation par catégorie', titleEn: 'Category booking', desc: 'SUV, berline, citadine...' },
      { icon: CheckCircle, title: 'Confirmation instantanée', titleEn: 'Instant confirmation', desc: 'Automatique' },
      { icon: Calendar, title: 'Calendrier interactif', titleEn: 'Interactive calendar', desc: 'Disponibilité en temps réel' },
      { icon: Baby, title: 'Options personnalisables', titleEn: 'Customizable options', desc: 'GPS, siège enfant, assurance' },
      { icon: Clock, title: 'Tarification forfaits', titleEn: 'Package pricing', desc: 'Jour, semaine, mois' },
      { icon: Percent, title: 'Prix saisonniers', titleEn: 'Seasonal pricing', desc: 'Promotions automatiques' },
    ]
  },
  {
    category: 'Paiement sécurisé',
    categoryEn: 'Secure Payment',
    icon: CreditCard,
    color: 'purple',
    features: [
      { icon: CreditCard, title: 'Carte bancaire (Stripe)', titleEn: 'Credit card (Stripe)', desc: 'Paiement en ligne sécurisé' },
      { icon: Banknote, title: 'Espèces', titleEn: 'Cash', desc: 'À la prise en charge' },
      { icon: Mail, title: 'Lien de paiement', titleEn: 'Payment link', desc: 'Envoyé par email' },
      { icon: Receipt, title: 'Confirmation automatique', titleEn: 'Auto confirmation', desc: 'Email instantané' },
    ]
  },
  {
    category: 'Contrats digitaux',
    categoryEn: 'Digital Contracts',
    icon: FileText,
    color: 'orange',
    features: [
      { icon: FileText, title: 'Génération PDF', titleEn: 'PDF generation', desc: 'Contrats automatiques' },
      { icon: PenTool, title: 'Signature électronique', titleEn: 'E-signature', desc: 'Mobile et web' },
      { icon: Send, title: 'Envoi automatique', titleEn: 'Auto-send', desc: 'Par email' },
    ]
  },
  {
    category: 'Inspections véhicules',
    categoryEn: 'Vehicle Inspections',
    icon: ClipboardCheck,
    color: 'teal',
    features: [
      { icon: ClipboardCheck, title: 'Check-in / Check-out', titleEn: 'Check-in / Check-out', desc: 'Digital avec checklist' },
      { icon: Fuel, title: 'Relevé kilométrique', titleEn: 'Mileage reading', desc: 'Et niveau carburant' },
      { icon: Camera, title: 'Photos d\'état', titleEn: 'Condition photos', desc: 'Documentation visuelle' },
      { icon: Sparkles, title: 'Détection IA', titleEn: 'AI detection', desc: 'Dommages automatiques' },
    ]
  },
  {
    category: 'Notifications & Emails',
    categoryEn: 'Notifications & Emails',
    icon: BellRing,
    color: 'pink',
    features: [
      { icon: Mail, title: 'Email confirmation', titleEn: 'Confirmation email', desc: 'Réservation & paiement' },
      { icon: BellRing, title: 'Rappel 24h', titleEn: '24h reminder', desc: 'Avant prise en charge' },
      { icon: AlertCircle, title: 'Rappel documents', titleEn: 'Document reminder', desc: 'CI + permis obligatoires' },
      { icon: MessageSquare, title: 'Notifications temps réel', titleEn: 'Real-time notifications', desc: 'Dans l\'application' },
    ]
  },
  {
    category: 'Suivi & Analytics',
    categoryEn: 'Tracking & Analytics',
    icon: TrendingUp,
    color: 'indigo',
    features: [
      { icon: BarChart3, title: 'Dashboard analytique', titleEn: 'Analytics dashboard', desc: 'Vue d\'ensemble avancée' },
      { icon: TrendingUp, title: 'Revenus par véhicule', titleEn: 'Revenue per vehicle', desc: 'Statistiques détaillées' },
      { icon: PieChart, title: 'Taux d\'occupation', titleEn: 'Occupancy rate', desc: 'De la flotte' },
      { icon: Activity, title: 'Tendances', titleEn: 'Trends', desc: 'Hebdomadaires' },
      { icon: CreditCard, title: 'Méthodes de paiement', titleEn: 'Payment methods', desc: 'Analyse détaillée' },
      { icon: CalendarRange, title: 'Planning Gantt', titleEn: 'Gantt planning', desc: 'Visualisation réservations' },
    ]
  },
  {
    category: 'Gestion clients',
    categoryEn: 'Client Management',
    icon: Users,
    color: 'cyan',
    features: [
      { icon: User, title: 'Espace client', titleEn: 'Client area', desc: 'Réservations, profil' },
      { icon: Upload, title: 'Upload documents', titleEn: 'Document upload', desc: 'CI, permis de conduire' },
      { icon: ScanFace, title: 'Vérification IA', titleEn: 'AI verification', desc: 'Documents d\'identité' },
      { icon: History, title: 'Historique complet', titleEn: 'Complete history', desc: 'Toutes les locations' },
    ]
  },
  {
    category: 'Sécurité & Contrôle',
    categoryEn: 'Security & Control',
    icon: Shield,
    color: 'red',
    features: [
      { icon: Shield, title: 'Contrôle par rôle', titleEn: 'Role-based control', desc: 'Super admin, admin, client' },
      { icon: Building2, title: 'Isolation agences', titleEn: 'Agency isolation', desc: 'Chaque agence ses données' },
      { icon: Lock, title: 'Données chiffrées', titleEn: 'Encrypted data', desc: 'Sécurité maximale' },
    ]
  },
  {
    category: 'Multi-plateforme',
    categoryEn: 'Multi-platform',
    icon: Smartphone,
    color: 'slate',
    features: [
      { icon: Monitor, title: 'Application web', titleEn: 'Web application', desc: 'Responsive design' },
      { icon: Smartphone, title: 'App mobile Android', titleEn: 'Android mobile app', desc: 'APK disponible' },
      { icon: Tablet, title: 'Interface admin', titleEn: 'Admin interface', desc: 'Dédiée par agence' },
    ]
  },
  {
    category: 'Localisation',
    categoryEn: 'Localization',
    icon: MapPin,
    color: 'emerald',
    features: [
      { icon: MapPin, title: 'Suivi GPS', titleEn: 'GPS tracking', desc: 'Temps réel' },
      { icon: Globe, title: 'Interface française', titleEn: 'French interface', desc: '100% en français' },
    ]
  },
];

const colorClasses = {
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', light: 'bg-blue-50' },
  green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200', light: 'bg-green-50' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', light: 'bg-purple-50' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200', light: 'bg-orange-50' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200', light: 'bg-pink-50' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200', light: 'bg-indigo-50' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', light: 'bg-cyan-50' },
  red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200', light: 'bg-red-50' },
  slate: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', light: 'bg-slate-50' },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
};

export const AllFeatures = () => {
  const { language } = useLanguage();
  const [expandedCategory, setExpandedCategory] = useState(null);

  const titles = {
    fr: {
      title: 'Toutes les fonctionnalités',
      subtitle: 'Une solution complète pour la gestion de votre flotte',
      features: 'fonctionnalités'
    },
    en: {
      title: 'All features',
      subtitle: 'A complete solution for your fleet management',
      features: 'features'
    }
  };

  const t = titles[language] || titles.fr;

  const toggleCategory = (index) => {
    setExpandedCategory(expandedCategory === index ? null : index);
  };

  return (
    <section id="all-features" className="py-16 md:py-24 bg-white" data-testid="all-features">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-base text-slate-600 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full">
            <span className="text-2xl font-bold text-slate-900">50+</span>
            <span className="text-slate-600">{t.features}</span>
          </div>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {allFeatures.map((category, index) => {
            const colors = colorClasses[category.color];
            const isExpanded = expandedCategory === index;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`rounded-xl border ${colors.border} overflow-hidden transition-all ${
                  isExpanded ? 'md:col-span-2 lg:col-span-1' : ''
                }`}
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(index)}
                  className={`w-full flex items-center justify-between p-4 ${colors.light} hover:brightness-95 transition-all`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${colors.bg} ${colors.text} flex items-center justify-center`}>
                      <category.icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-slate-900">
                        {language === 'en' ? category.categoryEn : category.category}
                      </h3>
                      <p className="text-xs text-slate-500">{category.features.length} {t.features}</p>
                    </div>
                  </div>
                  <div className={`w-8 h-8 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center`}>
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>

                {/* Features List */}
                <motion.div
                  initial={false}
                  animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 pt-0 space-y-2">
                    {category.features.map((feature, fIndex) => (
                      <div 
                        key={fIndex}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div className={`w-6 h-6 rounded-md ${colors.bg} ${colors.text} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                          <feature.icon className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="font-medium text-sm text-slate-900">
                            {language === 'en' ? feature.titleEn : feature.title}
                          </div>
                          <div className="text-xs text-slate-500">{feature.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Collapsed Preview */}
                {!isExpanded && (
                  <div className="px-4 pb-4">
                    <div className="flex flex-wrap gap-1">
                      {category.features.slice(0, 3).map((feature, fIndex) => (
                        <span 
                          key={fIndex}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs ${colors.bg} ${colors.text}`}
                        >
                          <Check className="w-3 h-3" />
                          {language === 'en' ? feature.titleEn : feature.title}
                        </span>
                      ))}
                      {category.features.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-slate-100 text-slate-600">
                          +{category.features.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
